import { BigNumber } from 'bignumber.js'
import findIndex from 'lodash/findIndex'
import type {
  BpCart,
  BpCartItem,
  LocationDeliveryOption,
  StoreResponse,
} from '~/definitions/AllDefinitions'
import { useMainStore } from '~/store/MainStore'
import { useOrderStore } from '~/store/OrderStore'
import { useUserStore } from '~/store/UserStore'
import { defaultError } from '~/classes/ErrorHandlers'
// import { DELIVERY_METHODS } from '~/definitions/enums'
import type { CartStoreInterface } from '~/definitions/StateDefinitions'
import { calcSubtotal } from '~/classes/CartUtils'

export const useCartStore = defineStore('cart-store', {
  persist: {
    pick: ['id', 'items'],
  },
  state: (): CartStoreInterface => ({
    id: null, // refers to cart uuid
    items: [],
    open: false,
  }),
  getters: {
    cartId: state => state.id,
    cartItems: state => state.items,
    cartCount: state => state.items && state.items.length ? state.items.length : 0,
    cartOpen: state => state.open,
    cartSubtotal(state): number {
      return calcSubtotal(state.items)
    },
    cartTotal(): number {
      const mainStore = useMainStore()
      const orderStore = useOrderStore()
      const currentLocation = mainStore.currentLocation
      if (!currentLocation) {
        return this.cartSubtotal
      }
      const subtotal = this.cartSubtotal
      let total = new BigNumber(0)
      total = total.plus(subtotal)
      total = total.plus(this.taxTotal)
      // if (orderStore.order.deliveryMethod === DELIVERY_METHODS.DELIVERY) {
      //   if (currentLocation.deliveryFeeType === 'all') {
      //     total = total.plus(currentLocation.defaultDeliveryFee)
      //   } else if (
      //     currentLocation.deliveryFeeType === 'free' &&
      //     currentLocation.freeDeliveryMin &&
      //     total.toNumber() < currentLocation.freeDeliveryMin
      //   ) {
      //     total = total.plus(currentLocation.defaultDeliveryFee)
      //   }
      // }
      if (orderStore.order.discountCode && orderStore.order.discountTotal) {
        total = total.minus(orderStore.order.discountTotal)
      }
      if (currentLocation.enableGratuity && orderStore.order.gratuityTotal) {
        total = total.plus(orderStore.order.gratuityTotal)
      }
      total = total.plus(this.deliveryTotal)
      // TODO calculate tip
      // if (rootGetters.enableGratuity && tip && tip.label) {
      //   if (tip.label === 'custom') {
      //     total += tip.value
      //   } else {
      //     const tipAmount = toFixedNoRound(subtotal * (tip.value / 100), 2, true)
      //     total += tipAmount
      //   }
      // }
      return total.toNumber()
    },
    discountTotal(): number {
      const orderStore = useOrderStore()
      return orderStore.order?.discountCode
        ? orderStore.order?.discountTotal ?? 0
        : 0
    },
    deliveryTotal(): number {
      const orderStore = useOrderStore()
      const { currentLocation, uberEatsDeliveryCost, doordashDeliveryCost } = useMainStore()
      let total = new BigNumber(0)
      if (orderStore && orderStore.isDelivery && currentLocation) {
        if (!(currentLocation && currentLocation.id)) {
          return 0
        }
        if (currentLocation.deliveryProvider === 'doordash') {
          const deliveryProviderOption = currentLocation.deliveryProviders.find(
            opt => opt.provider === currentLocation.deliveryProvider,
          ) as LocationDeliveryOption
          total = total.plus(doordashDeliveryCost)
          if (this.cartSubtotal < deliveryProviderOption.thresholdAmount) {
            return total.minus(deliveryProviderOption.deliveryTierOne).toNumber()
          } else {
            return total.minus(deliveryProviderOption.deliveryTierTwo).toNumber()
          }
        } else if (currentLocation.deliveryProvider === 'uberDirect') {
          const deliveryProviderOption = currentLocation.deliveryProviders.find(
            opt => opt.provider === currentLocation.deliveryProvider,
          ) as LocationDeliveryOption
          total = total.plus(uberEatsDeliveryCost)
          if (this.cartSubtotal < deliveryProviderOption.thresholdAmount) {
            return total.minus(deliveryProviderOption.deliveryTierOne).toNumber()
          } else {
            return total.minus(deliveryProviderOption.deliveryTierTwo).toNumber()
          }
        }
        return currentLocation.defaultDeliveryFee
      }
      return 0
    },
    taxTotal(): number {
      const mainStore = useMainStore()
      const currentLocation = mainStore.currentLocation
      if (!currentLocation) {
        return 0
      }
      const taxRate = new BigNumber(currentLocation.taxRate)
      const afterDiscountTotal = new BigNumber(this.cartSubtotal).minus(this.discountTotal)
      return afterDiscountTotal.times(taxRate).div(100).toNumber()
    },
  },
  actions: {
    userId() {
      const store = useUserStore()
      return store.userId ? store.userId : undefined
    },
    baseUrl() {
      const store = useMainStore()
      if (store.accountId && store.locationId) {
        return `/api/${store.accountId}/locations/${store.locationId}/carts`
      } else {
        return null
      }
    },
    clear() {
      this.id = null
      this.items.length = 0
      this.open = false
    },
    async createCart() {
      const url = this.baseUrl()
      if (!(url && !this.id)) {
        return defaultError
      }
      const body = {
        userId: this.userId(),
      } as any
      try {
        const data = await $fetch(url, { method: 'post', body })
        const ret = data as BpCart
        this.id = ret.uuid
        this.items = []
        return {
          success: true,
          error: null,
          data: ret,
        }
      } catch (err: any) {
        const formatError = useFormatError()
        return {
          success: false,
          error: formatError(err),
        }
      }
    },
    async createCartItem(body: any) {
      const url = this.baseUrl()
      if (!url) {
        return defaultError
      }
      const mainStore = useMainStore()
      const ret = await mainStore.checkIsOrderingEnabled()
      if (ret.error) {
        return ret
      }
      // Do cart lookup to verify that cart is still valid
      const cartRes = await this.getCart()
      if (cartRes.error) {
        // Cart is invalid; attempt to create a new cart
        const createCartRes = await this.createCart()
        if (createCartRes.error) {
          return createCartRes
        }
      }
      try {
        const data = await $fetch(`${url}/${this.id}/items`, {
          method: 'post',
          body,
        })
        const ret = data as BpCartItem
        this.items.push(ret)
        const { capture } = useTrackEvent()
        capture('item_added_to_cart', {
          itemName: body.name,
          quantity: body.quantity,
        })
        return {
          success: true,
          data: ret,
          error: null,
        }
      } catch (err: any) {
        const formatError = useFormatError()
        return {
          success: false,
          data: null,
          error: formatError(err),
        }
      }
    },
    async deleteCart() {
      const url = this.baseUrl()
      if (this.id && url) {
        try {
          await $fetch(`${url}/${this.id}`, { method: 'delete' })
          this.id = null
          this.items = []
          const { capture } = useTrackEvent()
          capture('cart_cleared')
          return {
            success: true,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
          }
        }
      } else {
        return defaultError
      }
    },
    async deleteCartItem(uuid: string): Promise<StoreResponse> {
      const url = this.baseUrl()
      if (!(url && this.id)) {
        return defaultError
      }
      try {
        await $fetch(`${url}/${this.id}/items/${uuid}`, {
          method: 'delete',
        })
        const { capture } = useTrackEvent()
        capture('cart_item_removed', {
          itemUuid: uuid,
        })
        return {
          success: true,
          data: null,
          error: null,
        }
      } catch (err: any) {
        const formatError = useFormatError()
        return {
          success: false,
          data: null,
          error: formatError(err),
        }
      }
    },
    async getCart(): Promise<StoreResponse> {
      if (!this.id) {
        return {
          success: false,
          data: null,
          error: {
            type: 'error',
            headline: 'No carts found',
            display: false,
            items: [],
          },
        }
      }
      let url = this.baseUrl()
      if (!url) {
        return defaultError
      }
      const params = {} as any
      let cart = null as BpCart | null
      if (this.id) {
        url += `/${this.id}`
      }
      if (this.userId()) {
        params.userId = this.userId()
      }
      try {
        const data = await $fetch(url, { params })
        if (this.id) {
          cart = data as BpCart
          this.id = cart.uuid
          this.items = cart.items && cart.items.length ? cart.items : []
        } else {
          const res = data as BpCart[]
          if (res.length) {
            this.id = res[0].uuid
            cart = res[0]
            this.items = cart.items && cart.items.length ? cart.items : []
          }
        }
        if (cart) {
          return {
            success: true,
            data: cart,
            error: null,
          }
        } else {
          this.id = null
          this.items = []
          return {
            success: false,
            data: null,
            error: {
              type: 'error',
              headline: 'No carts found',
              display: false,
              items: [],
            },
          }
        }
      } catch (err: any) {
        this.id = null
        this.items = []
        const formatError = useFormatError()
        return {
          success: false,
          data: null,
          error: formatError(err),
        }
      }
    },
    setCartIsOpen(open: boolean) {
      this.open = open
    },
    async updateCart(body: any): Promise<StoreResponse> {
      const url = this.baseUrl()
      if (!(url && this.id)) {
        return defaultError
      }
      const mainStore = useMainStore()
      await mainStore.checkIsOrderingEnabled()
      try {
        const data = await $fetch(`${url}/${this.id}`, {
          method: 'put',
          body,
        })
        return {
          success: true,
          data: data as BpCart,
          error: null,
        }
      } catch (err: any) {
        const formatError = useFormatError()
        return {
          success: false,
          error: formatError(err),
          data: null,
        }
      }
    },
    async updateCartItem(body: any, uuid: string): Promise<StoreResponse> {
      const url = this.baseUrl()
      if (!(url && this.id)) {
        return defaultError
      }
      const mainStore = useMainStore()
      await mainStore.checkIsOrderingEnabled()
      try {
        const data = await $fetch(`${url}/${this.id}/items/${uuid}`, {
          method: 'put',
          body,
        })
        const ret = data as BpCartItem
        const idx = findIndex(this.items, (o) => {
          return o.uuid === ret.uuid
        })
        if (idx > -1) {
          this.items.splice(idx, 1, ret)
        } else {
          await this.getCart()
        }
        const { capture } = useTrackEvent()
        capture('cart_item_updated', {
          itemUuid: uuid,
          quantity: body.quantity,
        })
        return {
          success: true,
          error: null,
          data: ret,
        }
      } catch (err: any) {
        const formatError = useFormatError()
        return {
          data: null,
          success: false,
          error: formatError(err),
        }
      }
    },
  },
})
